import { CommonModule } from '@angular/common';
import { Component, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Option } from '@interfaces/common/option';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { CompanyService } from '@services/http/company.service';
import { ToastHelperService } from '@services/helpers/toast-helper.service';
import { VaultBaseComponent } from '@shared/components/base/vault-base.component';
import { DialogComponent } from '@shared/components/dialog/dialog.component';
import { SharedModule } from '@shared/shared.module';
import { AppState } from '@state/index';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';

@Component({
  selector: 'app-onboarding-select-company-modal',
  standalone: true,
  templateUrl: './onboarding-select-company-modal.component.html',
  styleUrls: ['./onboarding-select-company-modal.component.scss'],
  imports: [CommonModule, FormsModule, SharedModule],
})
export class OnboardingSelectCompanyModalComponent extends VaultBaseComponent {
  @ViewChild('dialog') dialogRef: DialogComponent | null = null;

  companyOptions: Option[] = [];
  selectedCompanyId: string | null = null;

  constructor(
    translateService: TranslateService,
    store: Store<AppState>,
    private readonly companyService: CompanyService,
    private readonly router: Router,
    private readonly toast: ToastHelperService
  ) {
    super(translateService, store);
  }

  open(companyIds: string[]): void {
    this.selectedCompanyId = null;
    this.companyOptions = [];
    this.isLoading = true;
    this.dialogRef?.open('', '', '', 'Cancelar', '', 'onboarding-select-company', false, true);
    this.loadCompanies(companyIds);
  }

  isFormValid(): boolean {
    return !!this.selectedCompanyId;
  }

  confirm(): void {
    if (!this.selectedCompanyId) return;
    this.router.navigate(['/onboarding/company', this.selectedCompanyId]);
    this.dialogRef?.close();
  }

  cancel(): void {
    this.dialogRef?.close();
  }

  private loadCompanies(companyIds: string[]): void {
    this.companyService
      .getCompanys({ Page: 1, PageSize: companyIds.length }, null, null, companyIds)
      .subscribe({
        next: response => {
          const companies = response?.data ?? [];
          if (companies.length > 0) {
            this.companyOptions = companies.map(c => ({
              label: c.tradeName || c.displayName || c.id || '',
              value: c.id || '',
            }));
            this.isLoading = false;
          } else {
            this.loadCompaniesByIds(companyIds);
          }
        },
        error: () => this.loadCompaniesByIds(companyIds),
      });
  }

  private loadCompaniesByIds(companyIds: string[]): void {
    const requests = companyIds.map(id =>
      this.companyService.getCompanyById(id).pipe(catchError(() => of(null)))
    );

    forkJoin(requests).subscribe({
      next: companies => {
        this.companyOptions = companies.map((c, i) => ({
          label: c?.tradeName || c?.displayName || companyIds[i],
          value: c?.id || companyIds[i],
        }));
        this.isLoading = false;
      },
      error: () => {
        this.companyOptions = companyIds.map(id => ({ label: id, value: id }));
        this.isLoading = false;
      },
    });
  }
}
